import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import AuthHeader from './AuthHeader';

const EyeIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
    </svg>
);

const EyeOffIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
        <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
);

const PasswordField = ({ placeholder, value, onChange, disabled }) => {
    const [show, setShow] = useState(false);
    return (
        <div className="relative">
            <input
                type={show ? 'text' : 'password'}
                placeholder={placeholder}
                value={value}
                onChange={onChange}
                disabled={disabled}
                required
                className="w-full px-4 py-3 pr-11 rounded-xl border border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <button
                type="button"
                onClick={() => setShow((s) => !s)}
                className="absolute inset-y-0 right-3 flex items-center text-slate-400 hover:text-slate-600"
                tabIndex={-1}
            >
                {show ? <EyeOffIcon /> : <EyeIcon />}
            </button>
        </div>
    );
};

const ResetPassword = () => {
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const roboman_api = process.env.REACT_APP_ROBOMAN_API;

    const token = searchParams.get('token');

    const handleResetPassword = async (e) => {
        e.preventDefault();
        setMessage('');
        setError('');

        if (!token) {
            setError('Invalid or missing reset token. Please request a new password reset link.');
            return;
        }
        if (newPassword.length < 8) {
            setError('Password must be at least 8 characters long.');
            return;
        }
        if (newPassword !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(`${roboman_api}/users/password/change/${token}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    new_password: newPassword,
                    confirm_password: confirmPassword,
                }),
            });

            const data = await response.json();

            if (response.ok) {
                setMessage(data?.msg || 'Your password has been reset successfully.');
                setTimeout(() => navigate('/login'), 2000);
            } else {
                setError(data?.detail || 'Failed to reset password. The link may have expired.');
            }
        } catch (err) {
            console.error('Error:', err);
            setError('An error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex min-h-screen flex-col bg-white overflow-x-hidden">
            <AuthHeader />

            <div className="relative isolate flex-1 overflow-hidden">
                <div className="absolute inset-0 -z-10 bg-gradient-to-b from-indigo-50 via-white to-fuchsia-50" />
                <div className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 h-72 w-72 rounded-full bg-indigo-300/20 blur-3xl" />
                <div className="pointer-events-none absolute -bottom-24 right-0 h-96 w-96 rounded-full bg-fuchsia-300/20 blur-3xl" />

                <div className="mx-auto grid max-w-7xl grid-cols-1 lg:grid-cols-12 gap-0 px-4 sm:px-6 lg:px-8">

                    {/* Left brand panel */}
                    <aside className="relative hidden lg:flex lg:col-span-6 items-center py-12">
                        <div className="w-full rounded-3xl bg-white text-slate-800 ring-1 ring-slate-200 shadow-xl overflow-hidden">
                            <div className="relative p-10 sm:p-12">
                                <div
                                    className="absolute inset-0 opacity-60"
                                    style={{
                                        backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(2,6,23,.06) 1px, transparent 0)',
                                        backgroundSize: '22px 22px',
                                    }}
                                />
                                <div className="relative">
                                    <p className="text-xs tracking-[.25em] font-semibold uppercase text-fuchsia-600/70">
                                        Roboman
                                    </p>
                                    <h1 className="mt-2 text-4xl font-extrabold leading-tight text-slate-900">
                                        Choose a new password
                                    </h1>
                                    <p className="mt-3 text-slate-600 max-w-xl">
                                        You are almost there. Pick a strong password you have not used before and you will be back in your outreach cockpit in no time.
                                    </p>
                                    <ul className="mt-6 space-y-2">
                                        {[
                                            'At least 8 characters long',
                                            'Mix of uppercase and lowercase letters',
                                            'Include numbers or symbols',
                                            'Do not reuse an old password',
                                        ].map((tip) => (
                                            <li key={tip} className="flex items-center gap-2 text-sm text-slate-600">
                                                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                                                {tip}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </aside>

                    {/* Right form card */}
                    <main className="lg:col-span-6 flex items-center py-12">
                        <div className="w-full mx-auto max-w-md">
                            <div className="relative rounded-3xl bg-white/90 backdrop-blur-xl shadow-2xl ring-1 ring-slate-200 overflow-hidden">
                                {/* Gradient rail */}
                                <div className="h-[3px] w-full bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600" />

                                <div className="p-8 sm:p-10">
                                    <div className="text-center">
                                        <h2 className="text-2xl font-bold text-slate-900">Reset your password</h2>
                                        <p className="mt-1 text-sm text-slate-600">
                                            Enter and confirm your new password below
                                        </p>
                                    </div>

                                    {!token && (
                                        <div className="mt-5 p-3 rounded-xl bg-red-50 border border-red-200 text-center">
                                            <p className="text-red-600 text-sm">
                                                Invalid or expired reset link.{' '}
                                                <Link to="/forgotpw" className="font-semibold underline">
                                                    Request a new one
                                                </Link>.
                                            </p>
                                        </div>
                                    )}

                                    <form className="mt-6 space-y-4" onSubmit={handleResetPassword}>
                                        <PasswordField
                                            placeholder="New password (min. 8 characters)"
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            disabled={!token}
                                        />
                                        <PasswordField
                                            placeholder="Confirm new password"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            disabled={!token}
                                        />

                                        {message && (
                                            <p className="text-emerald-600 text-sm flex items-center gap-1.5">
                                                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                                                {message}
                                            </p>
                                        )}
                                        {error && (
                                            <p className="text-red-500 text-sm flex items-center gap-1.5">
                                                <span className="h-1.5 w-1.5 rounded-full bg-red-500 flex-shrink-0" />
                                                {error}
                                            </p>
                                        )}

                                        <button
                                            type="submit"
                                            disabled={loading || !token}
                                            className="relative inline-flex w-full items-center justify-center overflow-hidden rounded-xl px-5 py-3 text-sm font-semibold text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:opacity-60 disabled:cursor-not-allowed"
                                        >
                                            <span className="absolute inset-0 bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600" />
                                            <span className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity bg-[radial-gradient(100%_100%_at_50%_0%,rgba(255,255,255,.35)_0,rgba(255,255,255,0)_60%)]" />
                                            <span className="relative">
                                                {loading ? 'Resetting...' : 'Reset Password'}
                                            </span>
                                        </button>
                                    </form>

                                    <button
                                        className="w-full mt-4 rounded-xl px-4 py-3 text-sm font-semibold text-slate-800 bg-white ring-1 ring-slate-200 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                                        onClick={() => navigate('/login')}
                                    >
                                        Back to Login
                                    </button>

                                    <p className="text-center text-sm text-slate-600 mt-6">
                                        Remembered your password?{' '}
                                        <Link to="/login" className="font-semibold text-indigo-600 hover:text-indigo-700">
                                            Sign In
                                        </Link>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </main>
                </div>
            </div>
        </div>
    );
};

export default ResetPassword;